import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Navigation } from '@/components/Navigation';
import { BackButton } from '@/components/BackButton';
import { SEO } from '@/components/SEO';
import { Radio } from 'lucide-react';
import { toast } from 'sonner';
import { EarningsTracker } from '@/components/creator/EarningsTracker';
import { AnalyticsCharts } from '@/components/creator/AnalyticsCharts';
import { FanInsights } from '@/components/creator/FanInsights';
import { ContentManager } from '@/components/creator/ContentManager';
import { SubscriptionManager } from '@/components/creator/SubscriptionManager';
import { StreamAnalytics } from '@/components/creator/StreamAnalytics';
import { PayoutHistory } from '@/components/creator/PayoutHistory';
import { StreamScheduler } from '@/components/creator/StreamScheduler';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const CreatorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalEarnings: 0,
    monthlyEarnings: 0,
    totalStreams: 0,
  });
  const [recentStreams, setRecentStreams] = useState<any[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    if (!user) return;

    try {
      // Load wallet stats
      const { data: wallet, error: walletError } = await supabase
        .from('wallets')
        .select('total_earned')
        .eq('user_id', user.id)
        .maybeSingle();

      if (walletError) {
        console.error('Error loading wallet:', walletError);
      }

      // Load recent streams with better error handling
      const { data: streams, error: streamsError } = await supabase
        .from('streams')
        .select('*')
        .eq('creator_id', user.id)
        .order('created_at', { ascending: false })
        .limit(3);

      if (streamsError) {
        console.error('Error loading streams:', streamsError);
        toast.error('Failed to load stream data');
      }

      // Calculate monthly earnings from the last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: monthlyTips } = await supabase
        .from('tips')
        .select('amount')
        .eq('to_user_id', user.id)
        .gte('created_at', thirtyDaysAgo.toISOString());

      const monthlyEarnings = monthlyTips?.reduce((sum, tip) => sum + tip.amount, 0) || 0;

      setStats({
        totalEarnings: wallet?.total_earned || 0,
        monthlyEarnings,
        totalStreams: streams?.length || 0,
      });

      setRecentStreams(streams || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    }
  };

  return (
    <>
      <SEO 
        title="Creator Dashboard - Manage Your Live Streams & Earnings"
        description="Manage your live streams, track earnings, analyze engagement metrics, and grow your audience. Access powerful creator tools including subscription management, real-time analytics, payout history, and fan insights on HiStage."
        keywords={[
          "creator dashboard",
          "stream management",
          "earnings tracking",
          "creator analytics",
          "subscription management",
          "payout history",
          "fan insights",
          "live streaming tools"
        ]}
        noindex={true}
      />
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
      
      <div className="container py-8 max-w-7xl flex-1">
        <BackButton to="/" />
        <div className="space-y-8">
          {/* Go Live Section */}
          <Card className="glass-card">
            <CardContent className="p-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold mb-2">Ready to stream?</h2>
                  <p className="text-muted-foreground">Start broadcasting to your audience</p>
                </div>
                <Button size="lg" onClick={() => navigate('/go-live')} className="gradient-stage">
                  <Radio className="mr-2 h-5 w-5" />
                  GO LIVE
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Earnings Tracker */}
          <div>
            <h3 className="text-lg font-semibold mb-4">EARNINGS</h3>
            <EarningsTracker 
              totalCoins={stats.totalEarnings} 
              monthlyCoins={stats.monthlyEarnings}
            />
          </div>

          {/* Analytics Tabs */}
          <div>
            <h3 className="text-lg font-semibold mb-4">ANALYTICS</h3>
            <Tabs defaultValue="overview">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="streams">Stream Analytics</TabsTrigger>
                <TabsTrigger value="payouts">Payout History</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="mt-6">
                <AnalyticsCharts 
                  totalVotes={0}
                  totalTips={0}
                  avgSessionTime={0}
                  totalViewers={0}
                />
              </TabsContent>
              <TabsContent value="streams" className="mt-6">
                <StreamAnalytics />
              </TabsContent>
              <TabsContent value="payouts" className="mt-6">
                <PayoutHistory />
              </TabsContent>
            </Tabs>
          </div>

          {/* Subscription Management */}
          <div>
            <h3 className="text-lg font-semibold mb-4">SUBSCRIPTION TIERS</h3>
            <SubscriptionManager />
          </div>

          {/* Stream Scheduling */}
          <div>
            <h3 className="text-lg font-semibold mb-4">SCHEDULE STREAMS</h3>
            <StreamScheduler />
          </div>

          {/* Content Management */}
          <div>
            <h3 className="text-lg font-semibold mb-4">CONTENT MANAGEMENT</h3>
            <ContentManager />
          </div>

          {/* Fan Insights */}
          <FanInsights />

          {/* Recent Streams Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">RECENT STREAMS</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentStreams.map((stream) => (
                <Card key={stream.id} className="cursor-pointer hover:shadow-lg transition-all hover:scale-105">
                  <div className="aspect-video bg-muted relative overflow-hidden">
                    {stream.thumbnail_url && (
                      <img 
                        src={stream.thumbnail_url} 
                        alt={stream.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <CardContent className="p-4">
                    <p className="text-sm font-medium line-clamp-1">{stream.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(stream.created_at).toLocaleDateString()}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                        {stream.viewer_count || 0} viewers
                      </span>
                      <span className="text-xs bg-secondary/10 text-secondary px-2 py-1 rounded">
                        {stream.total_tips || 0} coins
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {recentStreams.length === 0 && (
                <div className="col-span-3 text-center py-12">
                  <Radio className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No streams yet</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => navigate('/go-live')}
                  >
                    Start Your First Stream
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default CreatorDashboard;
